package com.macrofocus.trenddisplay

import com.macrofocus.colormapping.implementation.SimpleColorMapping
import com.macrofocus.common.crossplatform.CPHelper
import com.macrofocus.common.properties.PropertyEvent
import com.macrofocus.common.properties.PropertyListener
import com.macrofocus.trenddisplay.timeseriesplot.DefaultTimeSeriesPlotModel
import com.macrofocus.trenddisplay.timeseriesplot.TimeSeriesPlotModel
import com.macrofocus.visual.SimpleVisual
import com.macrofocus.visual.SimpleVisualObjects
import org.mkui.colormap.ColorMapFactory
import org.mkui.component.CPFactory
import org.mkui.visual.Visual
import org.molap.aggregates.AggregateDataFrame
import org.molap.aggregates.aggregation.Aggregation
import org.molap.aggregates.aggregation.TimeSeriesAggregation
import org.molap.aggregates.cube.Group
import org.molap.dataframe.*
import org.molap.series.TimeSeries
import org.molap.subset.SubsetDataFrame

class TrendDisplayLogic<Row, Column, Value>(
    private val dataFrame: DataFrame<Row, Column, Value>,
    private val settings: com.macrofocus.trenddisplay.TrendDisplaySettings<Column>,
    private val cpFactory: CPFactory,
    private val colorMapFactory: ColorMapFactory
) {
    private val visual: Visual<Row, Column> =
        SimpleVisual(SimpleVisualObjects(dataFrame), SimpleColorMapping<Row, Column>(colorMapFactory, dataFrame))

    private val delegatedDataFrame: DelegatedDataFrame<Group, String, Any?>
    private val masterVisual: Visual<Group, String>
    private val subsetDataFrame: SubsetDataFrame<Group, String, Any?>

    init {
        this.delegatedDataFrame = DelegatedDataFrame()
        this.masterVisual = SimpleVisual(
            SimpleVisualObjects<Group>(delegatedDataFrame),
            SimpleColorMapping<Group, String>(colorMapFactory, delegatedDataFrame)
        )
        updateCache()
        delegatedDataFrame.printSchema()

        this.subsetDataFrame = SubsetDataFrame(delegatedDataFrame, masterVisual.filter, masterVisual.selection)

        settings.time.addPropertyListener(object : PropertyListener<Column?> {
            override fun propertyChanged(event: PropertyEvent<Column?>) {
                updateCache()
            }
        })

        settings.id.addPropertyListener(object : PropertyListener<Column?> {
            override fun propertyChanged(event: PropertyEvent<Column?>) {
                println("Id ${event.oldValue} -> ${event.oldValue} ${settings.id.value}")
                updateCache()
            }
        })

        //        if(dataFrame.getColumnIndex().contains((Column)"Name")) {
//            settings.getId().setValue((Column)"Name");
//        }
    }

    private fun updateCache() {
        if (settings.time.value != null) {
            val sortKeys: Array<SortKey<Column>>
            if (settings.id.value != null) {
                sortKeys = arrayOf<SortKey<Column>>(
                    SingleSortKey<Column>(settings.id.value!!, SortOrder.ASCENDING),
                    SingleSortKey<Column>(settings.time.value!!, SortOrder.ASCENDING)
                )
            } else {
                sortKeys = arrayOf<SortKey<Column>>(SingleSortKey(settings.time.value!!, SortOrder.ASCENDING))
            }
            val sorted: DataFrame<Row, Column, Value> = dataFrame!!.orderRows(*sortKeys)!!

            val aggregations: MutableList<Aggregation<*>> = ArrayList<Aggregation<*>>()
            if(settings.id.value != null) {
                aggregations.add(sorted.getFirst(settings.id.value!!).`as`(dataFrame.getColumnName(settings.id.value!!)))
            }
            aggregations.add(sorted.getMin(settings.time.value!!))
            aggregations.add(sorted.getMax(settings.time.value!!))
            aggregations.add(sorted.getDataFrameAggregation())
            for (key in dataFrame.columnIndex.keys()) {
                if (CPHelper.instance.isNumericType(dataFrame.getColumnClass(key))) {
                    aggregations.add(
                        TimeSeriesAggregation(
                            sorted.getColumn(settings.time.value!!)!!,
                            dataFrame.getColumn(key)!!
                        )
                    )
                }
            }

            //            final Aggregation[] aggregation = Iterables.<Aggregation>makeArray(aggregations);
//            for (Aggregation aggregation1 : aggregations.toArray(new Aggregation[aggregations.size()])) {
//                System.err.println("Aggregation: " + aggregation1);
//            }
            var aggregated: AggregateDataFrame<Column> = sorted.aggregate(true, *aggregations.toTypedArray() as Array<Aggregation<Any?>>)

            if (settings.id.value != null) {
                aggregated = aggregated.drillDown(settings.id.value!!)
            }
            println("Setting aggregation: $aggregated ${settings.id.value}")

            delegatedDataFrame.setDelegate(aggregated as DataFrame<Group, String, Any?>)
        }
    }

    fun getSettings(): com.macrofocus.trenddisplay.TrendDisplaySettings<Column> {
        return settings
    }

    fun getDataFrame(): DataFrame<Row, Column, Value> {
        return dataFrame
    }

    fun getVisual(): Visual<Row, Column> {
        return visual
    }

    val masterDataFrame: SubsetDataFrame<Group, String, Any?>
        get() = subsetDataFrame

    fun getMaxDuration(column: Column): Long {
        var maxDuration = 0L

        for (o in masterDataFrame.rows()) {
            val valueAt: TimeSeries<*,*> = masterDataFrame.getValueAt(o, column as String) as TimeSeries<*,*>
            val duration: Long = valueAt.maxTime - valueAt.minTime
            if (duration > maxDuration) {
                maxDuration = duration
            }
        }
        return maxDuration
    }

    fun getMaxEvents(column: Column): Int {
        var maxEvents = 0

        for (o in masterDataFrame.rows()) {
            val valueAt: TimeSeries<*,*> = masterDataFrame.getValueAt(o, column as String) as TimeSeries<*,*>
            val events: Int = valueAt.size()
            if (events > maxEvents) {
                maxEvents = events
            }
        }
        return maxEvents
    }

    fun getMasterVisual(): Visual<Group, String> {
        return masterVisual
    }

    fun createTimeSeriesPlotModel(): TimeSeriesPlotModel<Row, Column> {
        return DefaultTimeSeriesPlotModel<Row, Column>(cpFactory, colorMapFactory, this)
    }
}
